import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';

import { NOTIFICATION_CONFIG } from '@/shared/lib/constants';
import { dagKeys } from '@/shared/lib/query/queryKeys';

import { uploadLibraryApi } from '@/entities/dag/api';
import type { UploadLibraryRequest, UploadLibraryResponse } from '@/entities/dag/domain/types';
import type { ModuleTarget } from '@/entities/langserver/api/types';

type UploadLibraryInvalidateContext = {
  target: ModuleTarget;
  clientId: string;
};

type UseUploadLibraryMutationOptions = {
  invalidateModules?: UploadLibraryInvalidateContext;
  showNotification?: boolean;
};

export const useUploadLibraryMutation = (options?: UseUploadLibraryMutationOptions) => {
  const queryClient = useQueryClient();
  const showNotification = options?.showNotification ?? true;

  return useMutation<UploadLibraryResponse, Error, UploadLibraryRequest>({
    mutationFn: (request) => uploadLibraryApi(request),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: dagKeys.globalLibraries() });

      if (options?.invalidateModules) {
        await queryClient.invalidateQueries({
          queryKey: ['langserver', 'modules', options.invalidateModules.target, options.invalidateModules.clientId],
        });
      }

      if (showNotification) {
        notification.success({
          message: '라이브러리 업로드 성공',
          description: data.message,
          placement: NOTIFICATION_CONFIG.PLACEMENT,
          duration: NOTIFICATION_CONFIG.DURATION.SUCCESS,
        });
      }
    },
    onError: (error) => {
      console.error('Failed to upload library:', error);

      if (showNotification) {
        notification.error({
          message: '라이브러리 업로드 실패',
          description: error.message,
          placement: NOTIFICATION_CONFIG.PLACEMENT,
          duration: NOTIFICATION_CONFIG.DURATION.ERROR,
        });
      }
    },
  });
};
